import React from 'react';
import {
  Box, VStack, HStack, Card, CardBody, Text, Heading,
  FormControl, FormLabel, Switch, NumberInput, NumberInputField,
  NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, SliderMark,
  Badge, Progress, Stat, StatLabel, StatNumber, StatHelpText,
  Alert, AlertIcon, Button, Select, Divider, Grid, GridItem,
  useColorModeValue, Icon, Flex, Spacer
} from '@chakra-ui/react';
import { 
  FaWaveSquare, FaMicrophone, FaVolumeUp, FaClock, FaStopwatch,
  FaExclamationTriangle, FaChartLine, FaCog, FaPlayCircle
} from 'react-icons/fa';

const VoiceActivitySettings = ({
  vapiConfig,
  setVapiConfig,
  voiceActivityMetrics,
  isConnected,
  callMetrics,
  callDuration
}) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const updateVADConfig = (key, value) => {
    setVapiConfig(prev => ({
      ...prev,
      voiceActivityDetection: {
        ...prev.voiceActivityDetection,
        [key]: value
      }
    }));
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const getActivityColor = (level) => {
    if (level > 0.8) return 'red.400';
    if (level > 0.5) return 'yellow.400';
    if (level > 0.2) return 'green.400';
    return 'gray.300';
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Card bg={cardBg}>
        <CardBody>
          <HStack justify="space-between">
            <VStack align="start" spacing={1}>
              <Heading size="md">
                <Icon as={FaWaveSquare} mr={2} color="blue.500" />
                Voice Activity Detection
              </Heading>
              <Text fontSize="sm" color="gray.600">
                Real-time voice activity detection and interruption handling
              </Text>
            </VStack>
            <Badge 
              colorScheme={vapiConfig.voiceActivityDetection.enabled ? 'green' : 'gray'} 
              p={2} 
              borderRadius="md"
            >
              {vapiConfig.voiceActivityDetection.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </HStack>
        </CardBody>
      </Card>

      {/* Real-time Metrics */}
      {isConnected && (
        <Card bg={cardBg}>
          <CardBody>
            <Heading size="sm" mb={4}>
              <Icon as={FaChartLine} mr={2} />
              Real-time Activity Metrics
            </Heading>
            
            <Grid templateColumns="repeat(3, 1fr)" gap={4} mb={4}>
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Voice Level</StatLabel>
                  <StatNumber fontSize="md">
                    {(voiceActivityMetrics.currentLevel * 100).toFixed(1)}%
                  </StatNumber>
                  <Progress 
                    value={voiceActivityMetrics.currentLevel * 100}
                    colorScheme={voiceActivityMetrics.isSpeaking ? 'green' : 'blue'}
                    size="sm"
                    mt={1}
                  />
                </Stat>
              </GridItem>
              
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Status</StatLabel>
                  <StatNumber fontSize="sm">
                    <Badge colorScheme={voiceActivityMetrics.isSpeaking ? 'green' : 'gray'}>
                      {voiceActivityMetrics.isSpeaking ? 'Speaking' : 'Silent'}
                    </Badge>
                  </StatNumber>
                  <StatHelpText fontSize="xs">
                    Duration: {formatDuration(voiceActivityMetrics.speechDuration)}
                  </StatHelpText>
                </Stat>
              </GridItem>
              
              <GridItem>
                <Stat>
                  <StatLabel fontSize="xs">Interruptions</StatLabel>
                  <StatNumber fontSize="md">{voiceActivityMetrics.interruptions}</StatNumber>
                  <StatHelpText fontSize="xs">
                    {callDuration ? `${(voiceActivityMetrics.interruptions / (callDuration / 60)).toFixed(1)}/min` : '0/min'}
                  </StatHelpText>
                </Stat>
              </GridItem>
            </Grid>

            {callMetrics && (
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <Stat>
                    <StatLabel fontSize="xs">Call Duration</StatLabel>
                    <StatNumber fontSize="md">{formatDuration(callDuration * 1000)}</StatNumber>
                  </Stat>
                </GridItem>
                <GridItem>
                  <Stat>
                    <StatLabel fontSize="xs">Average Response</StatLabel>
                    <StatNumber fontSize="md">
                      {callMetrics.avgResponseTime ? `${callMetrics.avgResponseTime}ms` : 'N/A'}
                    </StatNumber>
                  </Stat>
                </GridItem>
              </Grid>
            )}
          </CardBody>
        </Card>
      )}

      {/* Core Settings */}
      <Card bg={cardBg}>
        <CardBody>
          <Heading size="sm" mb={4}>
            <Icon as={FaCog} mr={2} />
            Core Voice Activity Settings
          </Heading>
          
          <VStack spacing={6}>
            {/* Main VAD Toggle */}
            <FormControl>
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <FormLabel mb={0}>Enable Voice Activity Detection</FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    Enables real-time voice activity monitoring and interruption handling
                  </Text>
                </VStack>
                <Switch
                  isChecked={vapiConfig.voiceActivityDetection.enabled}
                  onChange={(e) => updateVADConfig('enabled', e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <Divider />

            {/* Interruption Settings */}
            <HStack spacing={8} w="100%">
              <FormControl flex="1">
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <FormLabel mb={0}>Enable Interruptions</FormLabel>
                    <Text fontSize="xs" color="gray.600">
                      Allow users to interrupt AI responses
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={vapiConfig.interruptionsEnabled}
                    onChange={(e) => setVapiConfig(prev => ({...prev, interruptionsEnabled: e.target.checked}))}
                    colorScheme="green"
                  />
                </HStack>
              </FormControl>

              <FormControl flex="1">
                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <FormLabel mb={0}>Backchanneling</FormLabel>
                    <Text fontSize="xs" color="gray.600">
                      AI gives verbal feedback during speech
                    </Text>
                  </VStack>
                  <Switch
                    isChecked={vapiConfig.backchannelingEnabled}
                    onChange={(e) => setVapiConfig(prev => ({...prev, backchannelingEnabled: e.target.checked}))}
                    colorScheme="purple"
                  />
                </HStack>
              </FormControl>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Advanced VAD Configuration */}
      <Card bg={cardBg}>
        <CardBody>
          <Heading size="sm" mb={4}>
            <Icon as={FaWaveSquare} mr={2} />
            Voice Activity Detection Parameters
          </Heading>
          
          <VStack spacing={6}>
            {/* Sensitivity Threshold */}
            <FormControl>
              <FormLabel>Detection Threshold</FormLabel>
              <HStack spacing={4}>
                <Slider
                  value={vapiConfig.voiceActivityDetection.threshold}
                  onChange={(value) => updateVADConfig('threshold', value)}
                  min={0.1}
                  max={1.0}
                  step={0.1}
                  flex="1"
                >
                  <SliderTrack>
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                  <SliderMark value={0.3} fontSize="xs" mt={2}>Low</SliderMark>
                  <SliderMark value={0.5} fontSize="xs" mt={2}>Med</SliderMark>
                  <SliderMark value={0.8} fontSize="xs" mt={2}>High</SliderMark>
                </Slider>
                <Badge colorScheme="blue" minW="50px">
                  {vapiConfig.voiceActivityDetection.threshold.toFixed(1)}
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.600" mt={1}>
                Lower = more sensitive to quiet speech | Higher = less sensitive to background noise
              </Text>
            </FormControl>

            {/* Timing Parameters */}
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Speech Timeout (ms)</FormLabel>
                  <NumberInput
                    value={vapiConfig.voiceActivityDetection.speechTimeoutMs}
                    onChange={(_, value) => updateVADConfig('speechTimeoutMs', value)}
                    min={1000}
                    max={10000}
                    step={500}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Max time to wait for speech to end
                  </Text>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Endpoint Timeout (ms)</FormLabel>
                  <NumberInput
                    value={vapiConfig.voiceActivityDetection.endpointingTimeoutMs}
                    onChange={(_, value) => updateVADConfig('endpointingTimeoutMs', value)}
                    min={500}
                    max={3000}
                    step={100}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Time to wait before ending speech detection
                  </Text>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Min Speech Duration (ms)</FormLabel>
                  <NumberInput
                    value={vapiConfig.voiceActivityDetection.minSpeechDurationMs}
                    onChange={(_, value) => updateVADConfig('minSpeechDurationMs', value)}
                    min={50}
                    max={1000}
                    step={50}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Minimum speech duration to register
                  </Text>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Max Speech Duration (ms)</FormLabel>
                  <NumberInput
                    value={vapiConfig.voiceActivityDetection.maxSpeechDurationMs}
                    onChange={(_, value) => updateVADConfig('maxSpeechDurationMs', value)}
                    min={10000}
                    max={60000}
                    step={5000}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Maximum continuous speech duration
                  </Text>
                </FormControl>
              </GridItem>
            </Grid>

            {/* Padding Settings */}
            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Pre-speech Padding (ms)</FormLabel>
                  <NumberInput
                    value={vapiConfig.voiceActivityDetection.preSpeechPaddingMs}
                    onChange={(_, value) => updateVADConfig('preSpeechPaddingMs', value)}
                    min={0}
                    max={1000}
                    step={50}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Audio buffer before speech starts
                  </Text>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel fontSize="sm">Post-speech Padding (ms)</FormLabel>
                  <NumberInput
                    value={vapiConfig.voiceActivityDetection.postSpeechPaddingMs}
                    onChange={(_, value) => updateVADConfig('postSpeechPaddingMs', value)}
                    min={0}
                    max={1000}
                    step={50}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Audio buffer after speech ends
                  </Text>
                </FormControl>
              </GridItem>
            </Grid>
          </VStack>
        </CardBody>
      </Card>

      {/* Response Timing Settings */}
      <Card bg={cardBg}>
        <CardBody>
          <Heading size="sm" mb={4}>
            <Icon as={FaClock} mr={2} />
            Response Timing Configuration
          </Heading>
          
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Silence Timeout (ms)</FormLabel>
                <NumberInput
                  value={vapiConfig.silenceTimeoutMs}
                  onChange={(_, value) => setVapiConfig(prev => ({...prev, silenceTimeoutMs: value}))}
                  min={500}
                  max={5000}
                  step={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Time to wait in silence before AI responds
                </Text>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Response Delay (ms)</FormLabel>
                <NumberInput
                  value={vapiConfig.responseDelayMs}
                  onChange={(_, value) => setVapiConfig(prev => ({...prev, responseDelayMs: value}))}
                  min={100}
                  max={2000}
                  step={50}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Delay before AI starts responding
                </Text>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">LLM Request Delay (ms)</FormLabel>
                <NumberInput
                  value={vapiConfig.llmRequestDelayMs}
                  onChange={(_, value) => setVapiConfig(prev => ({...prev, llmRequestDelayMs: value}))}
                  min={50}
                  max={1000}
                  step={25}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Delay before sending request to LLM
                </Text>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Max Call Duration (minutes)</FormLabel>
                <NumberInput
                  value={vapiConfig.maxCallDurationMs / 60000}
                  onChange={(_, value) => setVapiConfig(prev => ({...prev, maxCallDurationMs: value * 60000}))}
                  min={1}
                  max={60}
                  step={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Maximum allowed call duration
                </Text>
              </FormControl>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      {/* Audio Processing */}
      <Card bg={cardBg}>
        <CardBody>
          <Heading size="sm" mb={4}>
            <Icon as={FaVolumeUp} mr={2} />
            Audio Processing
          </Heading>
          
          <VStack spacing={4}>
            <FormControl>
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <FormLabel mb={0}>Background Noise Reduction</FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    Reduces background noise during speech detection
                  </Text>
                </VStack>
                <Switch
                  isChecked={vapiConfig.backgroundDenoisingEnabled}
                  onChange={(e) => setVapiConfig(prev => ({...prev, backgroundDenoisingEnabled: e.target.checked}))}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <VStack align="start" spacing={0}>
                  <FormLabel mb={0}>End Call on Extended Silence</FormLabel>
                  <Text fontSize="xs" color="gray.600">
                    Automatically end call after prolonged silence
                  </Text>
                </VStack>
                <Switch
                  isChecked={vapiConfig.endCallOnSilence}
                  onChange={(e) => setVapiConfig(prev => ({...prev, endCallOnSilence: e.target.checked}))}
                  colorScheme="orange"
                />
              </HStack>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Preset Configurations */}
      <Card bg={cardBg}>
        <CardBody>
          <Heading size="sm" mb={4}>
            <Icon as={FaPlayCircle} mr={2} />
            Quick Configuration Presets
          </Heading>
          
          <Alert status="info" mb={4}>
            <AlertIcon />
            <Text fontSize="sm">
              These presets will override your current voice activity settings with optimized configurations.
            </Text>
          </Alert>
          
          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
            <GridItem>
              <Button
                size="sm"
                variant="outline"
                colorScheme="green"
                onClick={() => {
                  setVapiConfig(prev => ({
                    ...prev,
                    interruptionsEnabled: true,
                    backchannelingEnabled: true,
                    silenceTimeoutMs: 800,
                    responseDelayMs: 300,
                    voiceActivityDetection: {
                      ...prev.voiceActivityDetection,
                      threshold: 0.3,
                      speechTimeoutMs: 2000,
                      endpointingTimeoutMs: 600
                    }
                  }));
                }}
                w="100%"
              >
                Conversational
              </Button>
              <Text fontSize="xs" color="gray.600" mt={1} textAlign="center">
                Natural, interruption-friendly
              </Text>
            </GridItem>
            
            <GridItem>
              <Button
                size="sm"
                variant="outline"
                colorScheme="blue"
                onClick={() => {
                  setVapiConfig(prev => ({
                    ...prev,
                    interruptionsEnabled: false,
                    backchannelingEnabled: false,
                    silenceTimeoutMs: 1500,
                    responseDelayMs: 500,
                    voiceActivityDetection: {
                      ...prev.voiceActivityDetection,
                      threshold: 0.6,
                      speechTimeoutMs: 4000,
                      endpointingTimeoutMs: 1200
                    }
                  }));
                }}
                w="100%"
              >
                Professional
              </Button>
              <Text fontSize="xs" color="gray.600" mt={1} textAlign="center">
                Formal, turn-based conversation
              </Text>
            </GridItem>
            
            <GridItem>
              <Button
                size="sm"
                variant="outline"
                colorScheme="purple"
                onClick={() => {
                  setVapiConfig(prev => ({
                    ...prev,
                    interruptionsEnabled: true,
                    backchannelingEnabled: false,
                    silenceTimeoutMs: 600,
                    responseDelayMs: 200,
                    voiceActivityDetection: {
                      ...prev.voiceActivityDetection,
                      threshold: 0.4,
                      speechTimeoutMs: 1500,
                      endpointingTimeoutMs: 500
                    }
                  }));
                }}
                w="100%"
              >
                Responsive
              </Button>
              <Text fontSize="xs" color="gray.600" mt={1} textAlign="center">
                Quick responses, high sensitivity
              </Text>
            </GridItem>
          </Grid>
        </CardBody>
      </Card>
    </VStack>
  );
};

export default VoiceActivitySettings;